import { QUALITY_TIER_RESOLUTION, type StreamResolution } from '../webrtcLatencyContracts';

const EXACT_PROFILES = Object.values(QUALITY_TIER_RESOLUTION);

/**
 * The encoded frame is the ShadowCam profile, or it is not sent.
 * scaleResolutionDownBy stays 1: a fraction is how 1280×720 becomes 457×257.
 */
export class SenderResolutionCap {
  private readonly limits = new WeakMap<RTCRtpSender, StreamResolution>();

  public remember(sender: RTCRtpSender, limit?: StreamResolution): void {
    if (!limit || limit.width <= 0 || limit.height <= 0) return;
    const previous = this.limits.get(sender);
    this.limits.set(sender, { width: Math.min(previous?.width ?? Infinity, limit.width), height: Math.min(previous?.height ?? Infinity, limit.height) });
  }

  /**
   * Always 1. A fractional scaleResolutionDownBy is how a 1280×720 capture becomes
   * 853×480 or 457×257. The ShadowCam profile is the only legal frame size.
   */
  public scale(_sender: RTCRtpSender): number {
    return 1;
  }

  /**
   * Require video capture to match a known ShadowCam size within the supplied pixel
   * bounds, and reset encoding scales to 1. Without a limit, only remembers state;
   * senders without settings or encodings are skipped at the corresponding step.
   *
   * @throws If reported dimensions are missing, unknown, or exceed the limit, or if
   *   a sender parameter operation fails. Earlier senders may already be updated.
   */
  public async enforce(connection: RTCPeerConnection, limit?: StreamResolution): Promise<void> {
    if (typeof connection.getSenders !== 'function') return;
    for (const sender of connection.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      this.remember(sender, limit);
      if (!limit) continue;
      const settings = sender.track.getSettings?.();
      if (!settings) continue;
      if (!settings.width || !settings.height) throw new Error('Video capture dimensions are unavailable');
      const exact = EXACT_PROFILES.some((profile) => profile.width === settings.width && profile.height === settings.height);
      // A shared session may already be on a lower exact profile. A larger capture, or any
      // size that is not a ShadowCam profile, must not be scaled into a new resolution.
      if (!exact || settings.width > limit.width || settings.height > limit.height) {
        throw new Error(
          `Capture is ${settings.width}x${settings.height}; the requested profile is ${limit.width}x${limit.height}`,
        );
      }
      const parameters = sender.getParameters() as RTCRtpSendParameters;
      if (!parameters.encodings?.length) continue;
      let changed = false;
      for (const encoding of parameters.encodings) {
        if (encoding.scaleResolutionDownBy !== 1) {
          encoding.scaleResolutionDownBy = 1;
          changed = true;
        }
      }
      if (parameters.degradationPreference !== 'maintain-resolution') {
        parameters.degradationPreference = 'maintain-resolution';
        changed = true;
      }
      if (changed) await sender.setParameters(parameters);
    }
  }
}
